/**
 * Dataverse table metadata tools
 */

import { z } from "zod";
import { displayValue } from "../utils/format.js";
import type { DataverseApi } from "../api/dataverse-api.js";
import { logger } from "../utils/logger.js";

// =============================================================================
// Tool Definitions
// =============================================================================

export const listDataverseTablesTool = {
  name: "list_dataverse_tables",
  description:
    "List Dataverse tables (entities) in the environment. Returns table names, entity set names, and whether they are custom or system tables. Use the EntitySetName in row operations.",
  inputSchema: {
    type: "object" as const,
    properties: {
      filter: {
        type: "string",
        description: "A keyword matched against logical/schema/display names (e.g. 'bot'), contains()/startswith()/endswith() on LogicalName, or an OData expression the metadata endpoint supports (e.g. 'IsCustomEntity eq true')",
      },
      top: {
        type: "number",
        description: "Maximum number of tables to return (default 100, max 250)",
        minimum: 1,
        maximum: 250,
      },
    },
  },
};

export const getDataverseTableTool = {
  name: "get_dataverse_table",
  description:
    "Get detailed metadata for a Dataverse table including all column definitions. Provide the table's logical name (e.g., 'account', 'contact'). Returns column names, types, and requirements.",
  inputSchema: {
    type: "object" as const,
    properties: {
      logicalName: {
        type: "string",
        description: "The logical name of the table (e.g., 'account', 'contact', 'cr4fd_mytable')",
      },
    },
    required: ["logicalName"],
  },
};

// =============================================================================
// Input Schemas
// =============================================================================

export const listDataverseTablesInputSchema = z.object({
  filter: z.string().optional(),
  top: z.number().min(1).max(250).optional(),
});

export const getDataverseTableInputSchema = z.object({
  logicalName: z.string().min(1),
});

// =============================================================================
// Tool Handlers
// =============================================================================

export interface DataverseTableToolsContext {
  api: DataverseApi | null;
}

function getDataverseNotAvailableMessage(): string {
  const initError = (globalThis as Record<string, unknown>).__dataverseInitError;
  const reason = initError ? `\n\nInit error: ${displayValue(initError)}` : "";
  return `Dataverse not available for this environment.

This typically means:
1. The environment doesn't have Dataverse provisioned
2. It's a "Default" environment without a Dataverse database

To use Dataverse table operations:
- Create an environment with a Dataverse database in Power Platform admin center
- Or provision Dataverse in your existing environment${reason}`;
}

export async function handleListDataverseTables(
  context: DataverseTableToolsContext,
  input: unknown
): Promise<string> {
  if (!context.api) return getDataverseNotAvailableMessage();

  const parsed = listDataverseTablesInputSchema.parse(input);
  logger.info({ filter: parsed.filter, top: parsed.top }, "Executing list_dataverse_tables");

  const tables = await context.api.listTables({ filter: parsed.filter, top: parsed.top });

  if (tables.length === 0) {
    return "No tables found matching the criteria.";
  }

  const cap = parsed.top ?? 100;
  // EntityDefinitions cannot page, so the API layer caps client-side. A list
  // that stops exactly at the cap is almost certainly truncated; saying so
  // stops "the table is not there" conclusions drawn from a capped list.
  const capped = tables.length >= cap;
  const lines = [
    capped
      ? `Dataverse Tables (first ${tables.length} — the environment has more; pass a larger top (max 250) or a filter — a keyword such as "bot", or OData such as "IsCustomEntity eq true" — to narrow it):`
      : `Dataverse Tables (${tables.length}):`,
  ];
  for (const table of tables) {
    const displayName = table.DisplayName?.UserLocalizedLabel?.Label || table.SchemaName;
    const custom = table.IsCustomEntity ? " [Custom]" : "";
    const managed = table.IsManaged ? " [Managed]" : "";
    lines.push(`  • ${displayName}${custom}${managed}`);
    lines.push(`    Logical Name: ${table.LogicalName}`);
    lines.push(`    Entity Set: ${table.EntitySetName}`);
    lines.push(`    Primary ID: ${table.PrimaryIdAttribute}`);
    if (table.PrimaryNameAttribute) {
      lines.push(`    Primary Name: ${table.PrimaryNameAttribute}`);
    }
  }

  return lines.join("\n");
}

export async function handleGetDataverseTable(
  context: DataverseTableToolsContext,
  input: unknown
): Promise<string> {
  if (!context.api) return getDataverseNotAvailableMessage();

  const parsed = getDataverseTableInputSchema.parse(input);
  logger.info({ logicalName: parsed.logicalName }, "Executing get_dataverse_table");

  let table: Awaited<ReturnType<DataverseApi["getTableMetadata"]>>["table"];
  let attributes: Awaited<ReturnType<DataverseApi["getTableMetadata"]>>["attributes"];
  try {
    ({ table, attributes } = await context.api.getTableMetadata(parsed.logicalName));
  } catch (error) {
    // Dataverse answers a missing table with its internal code and a message
    // about "EntityMetadata With Id = LogicalName=..." — say what it means and
    // what to try, since the usual cause is a display name or a plural.
    // The API layer wraps the response as ApiError("...: API error", status, details)
    // — the Dataverse code and text live in `details`, not in `.message`.
    const details = error && typeof error === "object" && "details" in error ? (error as { details?: unknown }).details : undefined;
    const message = `${error instanceof Error ? error.message : String(error)} ${details === undefined ? "" : typeof details === "string" ? details : JSON.stringify(details)}`;
    if (/0x80060888|does not exist/i.test(message)) {
      const guess = parsed.logicalName.trim().toLowerCase().replace(/\s+/g, "");
      const hint = guess !== parsed.logicalName ? ` Logical names are lowercase with no spaces (try '${guess}').` : "";
      return (
        `No table with logical name '${parsed.logicalName}' in the current environment.${hint} ` +
        `Logical names are singular (account, contact, cr866_mytable), not display names or entity set names; ` +
        `use list_dataverse_tables with a keyword filter such as "${guess.slice(0, 12)}" to find it, ` +
        `or switch_environment if the table lives in another environment.`
      );
    }
    throw error;
  }

  const displayName = table.DisplayName?.UserLocalizedLabel?.Label || table.SchemaName;
  const description = table.Description?.UserLocalizedLabel?.Label;

  const lines = [
    `Table: ${displayName} (${table.LogicalName})`,
    `Entity Set Name: ${table.EntitySetName}`,
    `Schema Name: ${table.SchemaName}`,
    `Primary ID: ${table.PrimaryIdAttribute}`,
  ];

  if (table.PrimaryNameAttribute) lines.push(`Primary Name: ${table.PrimaryNameAttribute}`);
  if (description) lines.push(`Description: ${description}`);
  lines.push(`Custom: ${table.IsCustomEntity}, Managed: ${table.IsManaged}`);

  lines.push(`\nColumns (${attributes.length}):`);
  for (const attr of attributes) {
    const attrDisplay = attr.DisplayName?.UserLocalizedLabel?.Label || attr.SchemaName;
    const required = attr.RequiredLevel?.Value === "ApplicationRequired" || attr.RequiredLevel?.Value === "SystemRequired"
      ? " *required*"
      : "";
    const custom = attr.IsCustomAttribute ? " [custom]" : "";
    lines.push(`  • ${attrDisplay} (${attr.LogicalName}) - ${attr.AttributeType}${required}${custom}`);
  }

  return lines.join("\n");
}
